import { hashKey, useQuery } from '@tanstack/react-query';
import { isEmpty } from 'lodash-es';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useAppPerformanceContext } from 'src/contexts/AppPerformanceContext';
import { ErrorTypes, useClientLogContext } from 'src/contexts/ClientLogContext';
import { PricingCalculationCacheTimeInMs } from 'src/utils/constants/constants';
import {
  BulkEditListingClient,
  Event,
  Feature,
  Listing,
  ListingPriceCalculationsInEventInput,
} from 'src/WebApiController';

import { useDebouncedValueSafe } from '../useDebouncedValueSafe';
import { useUserHasFeature } from '../useUserHasFeature';

export const useListingGroupAutoPricePreview = (
  event: Event,
  listings: Listing[],
  rawPricingSettingsUpdates: ListingPriceCalculationsInEventInput | undefined
) => {
  const hasAutoPricingFeature = useUserHasFeature(Feature.AutoPricing);
  const hasVeloCBFeature = useUserHasFeature(
    Feature.AutoPricingImprovedPricePreivewForVeloCB
  );
  const hasBulkPriceCalcCancellationFix = useUserHasFeature(
    Feature.BulkPriceCalcRequestCancellation
  );

  const { activeAccountWebClientConfig } = useAppContext();
  const { trackError } = useClientLogContext();
  const { trackEndpoint } = useAppPerformanceContext();

  const [debouncedPricingSettingsUpdate] = useDebouncedValueSafe(
    rawPricingSettingsUpdates
  );

  const listingIds = useMemo(() => listings.map((l) => l.id), [listings]);
  const maxVelocityCircuitBreakerStateRowVersion = useMemo(() => {
    const rowVersions = listings
      .map((listing) => listing.cirBrSuppRowVer)
      .filter((version) => version !== null && version !== undefined);

    return rowVersions.length ? Math.max(...rowVersions) : null;
  }, [listings]);

  const shouldQuery = Boolean(
    activeAccountWebClientConfig.activeAccountId &&
    event.viagId &&
    !isEmpty(listingIds) &&
    debouncedPricingSettingsUpdate &&
    hasAutoPricingFeature
  );

  const autoPricingSettingsUpdates = useMemo(():
    | ListingPriceCalculationsInEventInput
    | undefined => {
    if (!debouncedPricingSettingsUpdate) {
      return undefined;
    }
    const { rankPremium, ...rest } = debouncedPricingSettingsUpdate;
    // Temp solution to get pricing calculation with most updated rank premium,
    // Pricer uses the cached group pricing settings
    // Override the hasChanged to be true to passdown whatever from UI
    const result: ListingPriceCalculationsInEventInput = {
      ...rest,
      rankPremium: !rankPremium
        ? null
        : { hasChanged: true, value: rankPremium.value },
    };

    // Only include velocity circuit breaker max row version if feature is enabled
    if (hasVeloCBFeature) {
      result.velocityCircuitBreakerStateMaxRowVersion =
        maxVelocityCircuitBreakerStateRowVersion;
    }

    return result;
  }, [
    debouncedPricingSettingsUpdate,
    maxVelocityCircuitBreakerStateRowVersion,
    hasVeloCBFeature,
  ]);

  const listingCalculationsQuery = useQuery({
    queryKey: [
      'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
      activeAccountWebClientConfig.activeAccountId,
      event.viagId,
      hashKey([listingIds, autoPricingSettingsUpdates]),
    ],
    queryFn: async ({ signal }) => {
      const abortController = new AbortController();
      const effectiveSignal = hasBulkPriceCalcCancellationFix
        ? signal
        : abortController.signal;
      return await trackEndpoint(
        'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
        new BulkEditListingClient(
          activeAccountWebClientConfig
        ).bulkGetListingPriceCalculationsInEvent(
          event.viagId!,
          {
            item1: listingIds,
            item2: autoPricingSettingsUpdates!,
          },
          effectiveSignal
        ),
        (e: Error) => {
          abortController.abort(e.message);
        }
      );
    },
    retry: false,
    enabled: shouldQuery,
    refetchOnWindowFocus: false,
    // Cache results for 30s to avoid redundant API calls for identical inputs.
    // GC time matches stale time to bound memory usage, since each unique input
    // produces a large response that we don't want to retain longer than needed.
    staleTime: PricingCalculationCacheTimeInMs,
    gcTime: PricingCalculationCacheTimeInMs,
    meta: {
      onError: (error: ErrorTypes) => {
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        trackError(
          'BulkEditListingClient.BulkGetListingPriceCalculationsInEvent',
          error,
          {
            trackErrorData: {
              activeAccountId: activeAccountWebClientConfig.activeAccountId,
              viagogoEventId: event.viagId,
              listingIds,
              autoPricingSettingsUpdates,
            },
          }
        );
      },
    },
  });

  return useMemo(
    () => ({
      listingCalculationsQuery,
      bulkListingPriceCalculationResult: listingCalculationsQuery.data,
    }),
    [listingCalculationsQuery]
  );
};
